import _taggedTemplateLiteral from '@babel/runtime/helpers/esm/taggedTemplateLiteral';
import { css, useTheme, jsx } from '@emotion/react';
import { devWarning } from '@paypalcorp/pp-react-utils';
import { textStyles, CaptionText, BodyText } from '@paypalcorp/pp-react-text';
import { math } from 'polished';
import _extends from '@babel/runtime/helpers/esm/extends';
import _objectWithoutProperties from '@babel/runtime/helpers/esm/objectWithoutProperties';
import React, { useMemo } from 'react';
import { CloseIcon, CriticalIcon, ClockIcon, CheckmarkIcon } from '@paypalcorp/pp-react-icons';

var _templateObject, _templateObject2, _templateObject3, _templateObject4, _templateObject5, _templateObject6, _templateObject7, _templateObject8, _templateObject9, _templateObject10, _templateObject11, _templateObject12, _templateObject13, _templateObject14, _templateObject15, _templateObject16, _templateObject17, _templateObject18, _templateObject19, _templateObject20, _templateObject21, _templateObject22, _templateObject23, _templateObject24, _templateObject25, _templateObject26, _templateObject27, _templateObject28, _templateObject29, _templateObject30, _templateObject31, _templateObject32, _templateObject33, _templateObject34, _templateObject35, _templateObject36, _templateObject37, _templateObject38;

function styles(tokens) {
  /* istanbul ignore next */
  if (!Object.keys(tokens).length) {
    devWarning('Missing Emotion theme. Use ThemeProvider from @emotion/react to pass a valid theme from pp-react.');
    return {};
  }

  var sysColorPrimaryContrast = tokens.sysColorPrimaryContrast,
      sysColorStructureDivider = tokens.sysColorStructureDivider,
      sysColorBackgroundHighContrast = tokens.sysColorBackgroundHighContrast,
      sysColorBackgroundMediumContrast = tokens.sysColorBackgroundMediumContrast,
      sysColorBackgroundAlt = tokens.sysColorBackgroundAlt,
      sysColorBackgroundSurface = tokens.sysColorBackgroundSurface,
      sysColorSuccessMain = tokens.sysColorSuccessMain,
      sysColorWarningMain = tokens.sysColorWarningMain,
      sysColorErrorMain = tokens.sysColorErrorMain,
      sysSpaceJumbo = tokens.sysSpaceJumbo,
      sysSpaceLg = tokens.sysSpaceLg,
      sysSpaceMd = tokens.sysSpaceMd,
      sysSpaceXs = tokens.sysSpaceXs,
      sysSpaceTiny = tokens.sysSpaceTiny,
      sysSizeIconSm = tokens.sysSizeIconSm,
      sysSizeIconMd = tokens.sysSizeIconMd,
      sysSizeIconXs = tokens.sysSizeIconXs,
      sysBorderThicknessFocus = tokens.sysBorderThicknessFocus,
      sysBorderThicknessEmphasis = tokens.sysBorderThicknessEmphasis,
      sysBorderRadiusInteractive = tokens.sysBorderRadiusInteractive,
      sysSizeMediaMd = tokens.sysSizeMediaMd;
  var bgColorMap = {
    success: sysColorSuccessMain,
    warning: sysColorWarningMain,
    critical: sysColorErrorMain,
    cancelled: sysColorErrorMain,
    default: sysColorBackgroundSurface
  };

  var _textStyles = textStyles(tokens),
      textCaption = _textStyles.caption;

  var markerFn = function (type) {
    return css(_templateObject || (_templateObject = _taggedTemplateLiteral(["\n    // prettier-ignore\n    label: marker_", ";\n    &:after {\n      background-color: ", ";\n    }\n  "])), type, bgColorMap[type]);
  };

  var iconFn = function (type) {
    return css(_templateObject2 || (_templateObject2 = _taggedTemplateLiteral(["\n    // prettier-ignore\n    label: icon_", ";\n    --marker-outline-color: ", ";\n    background-color: ", ";\n  "])), type, bgColorMap[type], bgColorMap[type]);
  };

  var verticalStyles = {
    base: css(_templateObject3 || (_templateObject3 = _taggedTemplateLiteral(["\n      display: block;\n    "]))),
    item: css(_templateObject4 || (_templateObject4 = _taggedTemplateLiteral(["\n      flex-direction: row;\n      align-items: initial;\n    "]))),
    itemContent: css(_templateObject5 || (_templateObject5 = _taggedTemplateLiteral(["\n      margin: ", " 0;\n      text-align: initial;\n    "])), sysSpaceLg),
    itemContentWithoutDescription: css(_templateObject6 || (_templateObject6 = _taggedTemplateLiteral(["\n      display: inline-flex;\n      align-items: center;\n    "]))),
    marker: css(_templateObject7 || (_templateObject7 = _taggedTemplateLiteral(["\n      margin: 0 ", " 0 ", ";\n      padding: ", " 0;\n      &:after {\n        left: auto;\n        top: ", "; // Margin + Icon Size\n        width: ", "; // This is termed as a border for designers, hence used border token\n        height: 100%;\n        [dir='rtl'] & {\n          right: auto;\n        }\n      }\n    "])), sysSpaceLg, sysSpaceJumbo, sysSpaceLg, math("".concat(sysSizeIconMd, " + ").concat(sysSpaceLg)), sysBorderThicknessFocus)
  };
  var compactWrapper = css(_templateObject8 || (_templateObject8 = _taggedTemplateLiteral(["\n    --mobileOutdent: 0;\n    margin: 0;\n    @media screen and (max-width: ", ") {\n      padding: 0 ", ";\n    }\n  "])), sysSizeMediaMd, sysSpaceLg);
  var compactDetails = css(_templateObject9 || (_templateObject9 = _taggedTemplateLiteral(["\n    display: none;\n    @media screen and (max-width: ", ") {\n      display: block;\n      padding-top: ", ";\n      padding-bottom: ", ";\n    }\n  "])), sysSizeMediaMd, sysSpaceMd, sysSpaceMd);
  var compactLabel = css(_templateObject10 || (_templateObject10 = _taggedTemplateLiteral(["\n    color: ", ";\n  "])), sysColorBackgroundHighContrast);
  var compactDescription = css(_templateObject11 || (_templateObject11 = _taggedTemplateLiteral(["\n    color: ", ";\n  "])), sysColorBackgroundMediumContrast);
  var compactResponsiveItemContent = css(_templateObject12 || (_templateObject12 = _taggedTemplateLiteral(["\n    @media screen and (max-width: ", ") {\n      display: none;\n    }\n  "])), sysSizeMediaMd);
  var compactResponsiveMarker = css(_templateObject13 || (_templateObject13 = _taggedTemplateLiteral(["\n    @media screen and (max-width: ", ") {\n      &:after {\n        top: calc(", " - (0.5 * ", "));\n      }\n    }\n  "])), sysSizeMediaMd, sysSpaceMd, sysBorderThicknessEmphasis);
  var compactIcon = css(_templateObject14 || (_templateObject14 = _taggedTemplateLiteral(["\n    @media screen and (max-width: ", ") {\n      height: ", ";\n      width: ", ";\n      display: grid;\n      place-items: center;\n    }\n  "])), sysSizeMediaMd, sysSizeIconSm, sysSizeIconSm);
  var compactResponsiveBase = css(_templateObject15 || (_templateObject15 = _taggedTemplateLiteral(["\n    @media screen and (max-width: ", ") {\n      margin: 0 var(--mobileOutdent, 0);\n    }\n  "])), sysSizeMediaMd);
  var base = css(_templateObject16 || (_templateObject16 = _taggedTemplateLiteral(["\n    label: step_tracker_base;\n    color: ", ";\n    list-style: none;\n    margin: 0;\n    padding: 0;\n    display: grid;\n    grid-template-columns: repeat(auto-fit, minmax(0, 1fr));\n    &[data-stepper-variant='vertical'] {\n      ", "\n    }\n    @media screen and (max-width: ", ") {\n      &:not([data-stepper-mobile-variant='compact']) {\n        ", "\n      }\n    }\n  "])), sysColorBackgroundHighContrast, verticalStyles.base, sysSizeMediaMd, verticalStyles.base);
  var item = css(_templateObject17 || (_templateObject17 = _taggedTemplateLiteral(["\n    label: timeline_item;\n    display: flex;\n    position: relative;\n    flex-direction: column;\n    align-items: center;\n    z-index: 0;\n    &:last-of-type {\n      > div:first-of-type {\n        &:after {\n          display: none;\n        }\n      }\n    }\n    @media screen and (max-width: ", ") {\n      ul:not([data-stepper-mobile-variant='compact']) & {\n        ", "\n      }\n    }\n    [data-stepper-variant='vertical'] & {\n      ", "\n    }\n  "])), sysSizeMediaMd, verticalStyles.item, verticalStyles.item);
  var itemContent = css(_templateObject18 || (_templateObject18 = _taggedTemplateLiteral(["\n    label: timeline_item_content;\n    text-align: center;\n    margin-top: ", ";\n    @media screen and (max-width: ", ") {\n      ul:not([data-stepper-mobile-variant='compact']) & {\n        ", "\n      }\n    }\n    [data-stepper-variant='vertical'] & {\n      ", "\n    }\n  "])), sysSpaceLg, sysSizeMediaMd, verticalStyles.itemContent, verticalStyles.itemContent);
  var itemContentWithoutDescription = css(_templateObject19 || (_templateObject19 = _taggedTemplateLiteral(["\n    label: content_without_description;\n    @media screen and (max-width: ", ") {\n      ul:not([data-stepper-mobile-variant='compact']) & {\n        ", "\n      }\n    }\n    [data-stepper-variant='vertical'] & {\n      ", "\n    }\n  "])), sysSizeMediaMd, verticalStyles.itemContentWithoutDescription, verticalStyles.itemContentWithoutDescription);
  var description = css(_templateObject20 || (_templateObject20 = _taggedTemplateLiteral(["\n    label: description;\n    color: ", ";\n    margin-top: ", ";\n  "])), sysColorBackgroundMediumContrast, sysSpaceXs);
  var marker = css(_templateObject21 || (_templateObject21 = _taggedTemplateLiteral(["\n    label: marker;\n    color: ", ";\n    display: flex;\n    justify-content: center;\n    margin: 0;\n    padding: 0;\n    &:after {\n      position: absolute;\n      content: '';\n      inset-inline-start: 50%;\n      top: ", ";\n      width: 100%;\n      z-index: -1;\n      height: ", "; // This is termed as a border for designers, hence used border token\n      background-color: ", ";\n      [dir='rtl'] & {\n        left: auto;\n        right: calc(50% + ", ");\n      }\n    }\n    @media screen and (max-width: ", ") {\n      ul:not([data-stepper-mobile-variant='compact']) & {\n        ", "\n      }\n    }\n    [data-stepper-variant='vertical'] & {\n      ", "\n    }\n  "])), sysColorBackgroundAlt, sysSpaceLg, sysBorderThicknessFocus, sysColorStructureDivider, sysSpaceLg, sysSizeMediaMd, verticalStyles.marker, verticalStyles.marker);
  var currentMarker = css(_templateObject22 || (_templateObject22 = _taggedTemplateLiteral(["\n    outline-offset: ", ";\n    outline: ", " solid var(--marker-outline-color);\n    box-shadow: 0 0 0 ", " ", ";\n    z-index: 0;\n  "])), sysSpaceTiny, sysBorderThicknessEmphasis, sysSpaceXs, sysColorBackgroundSurface);
  var successMarker = css(_templateObject23 || (_templateObject23 = _taggedTemplateLiteral(["\n    ", "\n  "])), markerFn('success'));
  var warningMarker = css(_templateObject24 || (_templateObject24 = _taggedTemplateLiteral(["\n    ", "\n  "])), markerFn('warning'));
  var cancelledMarker = css(_templateObject25 || (_templateObject25 = _taggedTemplateLiteral(["\n    ", "\n  "])), markerFn('cancelled'));
  var criticalMarker = css(_templateObject26 || (_templateObject26 = _taggedTemplateLiteral(["\n    ", "\n  "])), markerFn('critical'));
  var iconItem = css(_templateObject27 || (_templateObject27 = _taggedTemplateLiteral(["\n    @media screen and (max-width: ", ") {\n      [data-stepper-mobile-variant='compact'] & {\n        height: ", ";\n        width: ", ";\n      }\n    }\n  "])), sysSizeMediaMd, sysSizeIconXs, sysSizeIconXs);
  var icon = css(_templateObject28 || (_templateObject28 = _taggedTemplateLiteral(["\n    label: icon;\n    border-radius: ", ";\n    height: ", ";\n    width: ", ";\n    padding: ", ";\n    @media screen and (max-width: ", ") {\n      [data-stepper-mobile-variant='compact'] & {\n        padding: 0;\n      }\n    }\n  "])), sysBorderRadiusInteractive, sysSizeIconMd, sysSizeIconMd, sysSpaceXs, sysSizeMediaMd);
  var cancelledIcon = css(_templateObject29 || (_templateObject29 = _taggedTemplateLiteral(["\n    ", "\n  "])), iconFn('cancelled'));
  var criticalIcon = css(_templateObject30 || (_templateObject30 = _taggedTemplateLiteral(["\n    ", "\n  "])), iconFn('critical'));
  var warningIcon = css(_templateObject31 || (_templateObject31 = _taggedTemplateLiteral(["\n    ", "\n  "])), iconFn('warning'));
  var successIcon = css(_templateObject32 || (_templateObject32 = _taggedTemplateLiteral(["\n    ", "\n  "])), iconFn('success'));
  var defaultIcon = css(_templateObject33 || (_templateObject33 = _taggedTemplateLiteral(["\n    ", "\n    --marker-outline-color: ", ";\n    border: ", " solid ", ";\n    padding: ", " ", ";\n  "])), iconFn('default'), sysColorStructureDivider, sysBorderThicknessFocus, sysColorStructureDivider, sysSpaceXs, math("".concat(sysSpaceLg, " - ").concat(sysBorderThicknessFocus)));
  var numeric = css(_templateObject34 || (_templateObject34 = _taggedTemplateLiteral(["\n    label: numeric;\n    text-align: center;\n  "])));
  var numericDefault = css(_templateObject35 || (_templateObject35 = _taggedTemplateLiteral(["\n    label: numeric_default;\n    background-color: ", ";\n    padding: 0;\n  "])), sysColorBackgroundAlt);
  var numericSuccess = css(_templateObject36 || (_templateObject36 = _taggedTemplateLiteral(["\n    label: numeric_success;\n    color: ", ";\n  "])), sysColorPrimaryContrast);
  var numericText = css(_templateObject37 || (_templateObject37 = _taggedTemplateLiteral(["\n    @media screen and (max-width: ", ") {\n      [data-stepper-mobile-variant='compact'] & {\n        label: numeric_text_compact;\n        ", ";\n      }\n    }\n  "])), sysSizeMediaMd, textCaption());
  var numericTextSuccess = css(_templateObject38 || (_templateObject38 = _taggedTemplateLiteral(["\n    @media screen and (max-width: ", ") {\n      [data-stepper-mobile-variant='compact'] & {\n        label: numeric_text_success_compact;\n        ", ";\n      }\n    }\n  "])), sysSizeMediaMd, textCaption({
    color: sysColorPrimaryContrast
  }));
  return {
    base: base,
    item: item,
    itemContent: itemContent,
    itemContentWithoutDescription: itemContentWithoutDescription,
    description: description,
    marker: marker,
    currentMarker: currentMarker,
    successMarker: successMarker,
    warningMarker: warningMarker,
    cancelledMarker: cancelledMarker,
    criticalMarker: criticalMarker,
    icon: icon,
    iconItem: iconItem,
    numericText: numericText,
    numericTextSuccess: numericTextSuccess,
    cancelledIcon: cancelledIcon,
    criticalIcon: criticalIcon,
    warningIcon: warningIcon,
    successIcon: successIcon,
    defaultIcon: defaultIcon,
    numeric: numeric,
    numericSuccess: numericSuccess,
    numericDefault: numericDefault,
    compactWrapper: compactWrapper,
    compactResponsiveBase: compactResponsiveBase,
    compactDetails: compactDetails,
    compactLabel: compactLabel,
    compactDescription: compactDescription,
    compactResponsiveItemContent: compactResponsiveItemContent,
    compactResponsiveMarker: compactResponsiveMarker,
    compactIcon: compactIcon
  };
}

var _excluded = ["className", "children", "showDescription", "desktopVariant", "mobileVariant"];

var ProgressStepper = function (_ref) {
  var className = _ref.className,
      children = _ref.children,
      _ref$showDescription = _ref.showDescription,
      showDescription = _ref$showDescription === void 0 ? 'all' : _ref$showDescription,
      desktopVariant = _ref.desktopVariant,
      _ref$mobileVariant = _ref.mobileVariant,
      mobileVariant = _ref$mobileVariant === void 0 ? 'vertical' : _ref$mobileVariant,
      rest = _objectWithoutProperties(_ref, _excluded);

  var tokens = useTheme();
  var sysSizeIconMd = tokens.sysSizeIconMd;

  var _styles = useMemo(function () {
    return styles(tokens);
  }, [tokens]);

  var childrenMap;
  var compactDetails;
  var childCount = React.Children.count(children); // Steps are divided into n boxes that each get 1/n of the total width
  // First step is centered at 1/(2n)
  // So formula for inset is 1/(2n) - radius
  // We set that as padding and allow browser to keep left edge lined up
  // with the edge of the first and last steps.   --BCC

  var markerInset = "calc(-".concat(100 / ((childCount - 1) * 2), "% + (0.5 * ").concat(sysSizeIconMd, "))");
  var isCurrentDetected = false;
  var statusQueue = [];
  React.Children.map(children, function (child) {
    var status = child.props.status;
    statusQueue.push(status || 'default');
  });
  childrenMap = React.Children.map(children, function (child, index) {
    var _child$props = child.props,
        isCurrent = _child$props.isCurrent,
        isDescriptionEnabled = _child$props.isDescriptionEnabled; // Only add this if we want the compact variant
    // This may or may not be hidden depending on viewport size

    if (mobileVariant === 'compact' && isCurrent) {
      compactDetails = jsx("div", {
        css: _styles.compactDetails,
        "data-ppui": "true"
      }, jsx(CaptionText, {
        css: _styles.compactLabel,
        "data-ppui": "true"
      }, child.props.title), jsx(CaptionText, {
        css: _styles.compactDescription,
        "data-ppui": "true"
      }, child.props.children));
    } // Decide when to display the description giving priority to override


    if (isDescriptionEnabled === undefined) {
      // Always display Current unless display option is "none" or is overridden
      if (isCurrent) {
        isDescriptionEnabled = true;
        isCurrentDetected = true;
      }

      if (showDescription === 'all' || showDescription === 'current' && isCurrent || showDescription === 'afterCurrent' && isCurrentDetected === true || showDescription === 'beforeCurrent' && isCurrentDetected === false) {
        isDescriptionEnabled = true;
      } else if (showDescription === 'none') {
        isDescriptionEnabled = false;
      }
    }

    var currentStep = index + 1;
    var nextStatus = statusQueue[currentStep] === 'default' && statusQueue[index] === 'success' ? 'success' : statusQueue[currentStep];
    return React.cloneElement(child, {
      isDescriptionEnabled: isDescriptionEnabled,
      status: statusQueue[index],
      nextStatus: nextStatus,
      mobileVariant: mobileVariant,
      index: currentStep,
      'aria-setsize': statusQueue.length,
      'aria-posinset': currentStep
    });
  });
  var wrapperCSS = [mobileVariant === 'compact' && _styles.compactWrapper];
  var listCSS = [_styles.base, mobileVariant === 'compact' && _styles.compactResponsiveBase];
  return jsx("div", {
    css: wrapperCSS,
    style: {
      '--mobileOutdent': markerInset
    },
    "data-ppui": "true"
  }, jsx("ul", _extends({
    css: listCSS,
    className: className,
    "data-stepper-variant": desktopVariant,
    "data-stepper-mobile-variant": mobileVariant,
    "data-ppui-info": "progress-stepper_2.4.2"
  }, rest), childrenMap), compactDetails);
};

var _excluded$1 = ["title", "status", "ariaLabel", "isCurrent", "isDescriptionEnabled", "children", "nextStatus", "mobileVariant", "className", "isNumeric", "index"];

// eslint-disable-next-line complexity
var Step = function (_ref) {
  var _ref$title = _ref.title,
      title = _ref$title === void 0 ? 'no title' : _ref$title,
      status = _ref.status,
      ariaLabel = _ref.ariaLabel,
      isCurrent = _ref.isCurrent,
      _ref$isDescriptionEna = _ref.isDescriptionEnabled,
      isDescriptionEnabled = _ref$isDescriptionEna === void 0 ? undefined : _ref$isDescriptionEna,
      children = _ref.children,
      nextStatus = _ref.nextStatus,
      mobileVariant = _ref.mobileVariant,
      className = _ref.className,
      _ref$isNumeric = _ref.isNumeric,
      isNumeric = _ref$isNumeric === void 0 ? false : _ref$isNumeric,
      index = _ref.index,
      rest = _objectWithoutProperties(_ref, _excluded$1);

  var tokens = useTheme();

  var _styles = useMemo(function () {
    return styles(tokens);
  }, [tokens]);

  var mapping = {
    cancelled: jsx(CloseIcon, {
      css: _styles.iconItem,
      size: "sm",
      color: "sysColorErrorContrast",
      "data-ppui": "true"
    }),
    critical: jsx(CriticalIcon, {
      css: _styles.iconItem,
      size: "sm",
      color: "sysColorErrorContrast",
      "data-ppui": "true"
    }),
    warning: jsx(ClockIcon, {
      css: _styles.iconItem,
      size: "sm",
      color: "sysColorWarningContrast",
      "data-ppui": "true"
    }),
    success: isNumeric ? jsx(BodyText, {
      css: [_styles.numeric, _styles.numericSuccess, mobileVariant === 'compact' && _styles.numericTextSuccess],
      "data-ppui": "true"
    }, index) : jsx(CheckmarkIcon, {
      css: _styles.iconItem,
      size: "sm",
      color: "sysColorSuccessContrast",
      "data-ppui": "true"
    }),
    default: isNumeric ? jsx(BodyText, {
      css: [_styles.numeric, mobileVariant === 'compact' && _styles.numericText],
      "data-ppui": "true"
    }, index) : jsx(React.Fragment, null)
  };
  var statusName = mapping[status] ? status : 'default';
  var nextStatusName = mapping[nextStatus] ? nextStatus : 'default';
  var itemContentCSS = [_styles.itemContent, mobileVariant === 'compact' && _styles.compactResponsiveItemContent, (!isDescriptionEnabled || !children) && _styles.itemContentWithoutDescription];
  var markerCSS = [_styles.marker, mobileVariant === 'compact' && _styles.compactResponsiveMarker, //@ts-ignore
  nextStatusName && _styles[nextStatusName + 'Marker']];
  var iconCSS = [_styles.icon, isCurrent && _styles.currentMarker, mobileVariant === 'compact' && _styles.compactIcon, //@ts-ignore
  _styles[statusName + 'Icon'], isNumeric && statusName === 'default' && _styles.numericDefault];
  return jsx("li", _extends({
    css: _styles.item,
    className: className,
    "aria-current": !!isCurrent
  }, rest, {
    "data-ppui": "true"
  }), jsx("div", {
    css: markerCSS,
    "aria-hidden": !ariaLabel,
    "data-ppui": "true"
  }, jsx("div", {
    role: ariaLabel && 'img',
    "aria-label": ariaLabel,
    css: iconCSS,
    "data-ppui": "true"
  }, mapping[statusName])), jsx("div", {
    css: itemContentCSS,
    "data-ppui": "true"
  }, jsx(BodyText, {
    strong: !!isCurrent,
    "data-ppui": "true"
  }, title), isDescriptionEnabled && children && jsx(CaptionText, {
    css: _styles.description,
    "data-ppui": "true"
  }, children)));
};

export { ProgressStepper, Step, styles as progressStepperStyles };
